<template>

  <div>
    <h4>Para Candidatos</h4>
    <p class="text-muted">Configure o agente que conversa com os candidatos. Defina o tom das conversas, o que ele consegue fazer e como cada fluxo é executado.</p>

    <div class="form-group">
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          id="candidate_is_enabled"
          v-model="value.is_enabled">
        <label
          class="form-check-label"
          for="candidate_is_enabled">
          Habilitar agente para candidatos
          </label>
      </div>
      <ValidateMessage :error="error('is_enabled')" />
      <p class="text-muted">Chave geral do agente. Com ela desligada o agente não responde nenhuma conversa, mesmo com capacidades ou fluxos configurados.</p>
    </div>

    <div v-if="value.is_enabled">
      <div
        class="form-group">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="candidate_auto_assign_to_new_conversations"
            v-model="value.auto_assign_to_new_conversations">
          <label
            class="form-check-label"
            for="candidate_auto_assign_to_new_conversations">
            Atribuir o agente automaticamente a novas conversas
            </label>
        </div>
        <ValidateMessage :error="error('auto_assign_to_new_conversations')" />
        <p class="text-muted">Novas conversas já começam com o agente ativo. Com a opção desligada, um recrutador precisa atribuir o agente à conversa.</p>
      </div>

      <div class="form-group">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="candidate_auto_link_candidate_to_conversation"
            v-model="value.auto_link_candidate_to_conversation">
          <label
            class="form-check-label"
            for="candidate_auto_link_candidate_to_conversation">
            Vincular candidato à conversa automaticamente
            </label>
        </div>
        <ValidateMessage :error="error('auto_link_candidate_to_conversation')" />
        <p class="text-muted">Atribui automaticamente o candidato à conversa na primeira mensagem dele ou quando ele se inscreve em uma vaga.</p>
      </div>

      <div class="form-group">
        <label>Tom de voz</label>
        <select
          class="form-control"
          v-model="value.tone">
          <option
            v-for="option in toneOptions"
            :key="option.value"
            :value="option.value">
            {{ option.name }}
          </option>
        </select>
        <ValidateMessage :error="error('tone')" />
        <p class="text-muted">Aplicado tanto na conversa livre quanto nos fluxos.</p>
      </div>

      <div class="form-group">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="candidate_human_handoff_is_enabled"
            v-model="value.human_handoff.is_enabled">
          <label
            class="form-check-label"
            for="candidate_human_handoff_is_enabled">
            Habilitar transferência para atendimento humano
            </label>
        </div>
        <ValidateMessage :error="error('human_handoff.is_enabled')" />
        <p class="text-muted">O agente transfere a conversa para um recrutador quando não conseguir resolver a solicitação.</p>
      </div>

      <h5>Capacidades</h5>
      <p class="text-muted">O que o agente consegue fazer na conversa livre. Um recrutador sempre pode atribuir o agente a uma conversa.</p>

      <div class="form-group">
        <div
          class="form-check"
          v-for="capability in capabilityOptions"
          :key="capability.value">
          <input
            class="form-check-input"
            type="checkbox"
            :id="`candidate_capability_${capability.value}`"
            :value="capability.value"
            :disabled="capability.fixed"
            v-model="value.mode.capabilities">
          <label
            class="form-check-label"
            :for="`candidate_capability_${capability.value}`">
            {{ capability.name }}
            </label>
          <p class="text-muted">{{ capability.description }}</p>
        </div>
        <ValidateMessage :error="error('mode.capabilities')" />
      </div>

      <div class="form-group">
        <label for="application_whatsapp_number">WhatsApp padrão de candidatura</label>
        <input
          class="form-control"
          type="text"
          id="application_whatsapp_number"
          v-model="value.application_whatsapp_number"
          placeholder="+551100000000">
        <ValidateMessage :error="error('application_whatsapp_number')" />
        <p class="text-muted">Número do WhatsApp configurado nas integrações que será usado no site de carreiras para inscrições em vagas. Ele precisa estar integrado e ativo ao Quickin.</p>
      </div>

      <h5>Configuração dos fluxos</h5>
      <p class="text-muted">Estas configurações são aplicadas sempre que o fluxo for executado.</p>

      <div class="form-group">
        <label>Candidatura &mdash; campos do perfil coletados</label>
        <div
          class="form-check"
          v-for="field in profileFieldOptions"
          :key="field.value">
          <input
            class="form-check-input"
            type="checkbox"
            :id="`candidate_profile_field_${field.value}`"
            :value="field.value"
            v-model="value.flows.application.config.profile_fields">
          <label
            class="form-check-label"
            :for="`candidate_profile_field_${field.value}`">
            {{ field.name }}
            </label>
        </div>
        <ValidateMessage :error="error('flows.application.config.profile_fields')" />
      </div>
    </div>

  </div>

</template>

<script>
/*
defaultCandidate
estrutura completa de conversational_agent.candidate
*/
export const defaultCandidate = () => ({
  is_enabled: false,
  auto_assign_to_new_conversations: false,
  auto_link_candidate_to_conversation: false,
  tone: 'neutral',
  default_whatsapp_number: '',
  human_handoff: {
    is_enabled: false,
    config: {}
  },
  mode: {
    capabilities: ['discovery_and_apply']
  },
  flows: {
    application: {
      config: {
        profile_fields: []
      }
    },
    interview: {
      config: {}
    },
    scheduling: {
      config: {}
    },
    questionnaire: {
      config: {}
    }
  }
})

/*
mergeFlow
*/
const mergeFlow = (defaults, data) => {
  const flow = data || {}

  return {
    ...defaults,
    ...flow,
    config: {
      ...defaults.config,
      ...flow.config
    }
  }
}

/*
mergeCandidate
garante a estrutura completa mesmo quando a api retorna campos parciais
*/
export const mergeCandidate = (data) => {
  const defaults = defaultCandidate()

  if (!data) {
    return defaults
  }

  const humanHandoff = data.human_handoff || {}
  const mode = data.mode || {}
  const flows = data.flows || {}
  const application = flows.application || {}

  return {
    ...defaults,
    ...data,
    human_handoff: {
      ...defaults.human_handoff,
      ...humanHandoff,
      config: {
        ...defaults.human_handoff.config,
        ...humanHandoff.config
      }
    },
    mode: {
      ...defaults.mode,
      ...mode,
      // discovery_and_apply e fixa, sempre volta habilitada
      capabilities: ['discovery_and_apply'].concat((mode.capabilities || []).filter(capability => capability !== 'discovery_and_apply'))
    },
    flows: {
      application: {
        ...defaults.flows.application,
        ...application,
        config: {
          ...defaults.flows.application.config,
          ...application.config,
          profile_fields: (application.config || {}).profile_fields || []
        }
      },
      interview: mergeFlow(defaults.flows.interview, flows.interview),
      scheduling: mergeFlow(defaults.flows.scheduling, flows.scheduling),
      questionnaire: mergeFlow(defaults.flows.questionnaire, flows.questionnaire)
    }
  }
}

export default {
  name: 'AccountConversationalAgentCandidate',
  props: {
    value: {
      type: Object,
      required: true
    },
    validateErrors: {
      type: Object,
      default: () => ({})
    },
    errorPrefix: {
      type: String,
      default: 'conversational_agent.candidate'
    }
  },
  data () {
    return {
      toneOptions: [
        {value: 'friendly', name: 'Amigável'},
        {value: 'neutral', name: 'Neutro'},
        {value: 'direct', name: 'Direto'},
        {value: 'professional', name: 'Profissional'}
      ],
      capabilityOptions: [
        {
          value: 'discovery_and_apply',
          name: 'Descobrir vagas e candidatar-se',
          description: 'O agente sugere vagas e conduz a candidatura do candidato.',
          fixed: true
        }
        // {
        //   value: 'knowledge_search',
        //   name: 'Pesquisar na base de conhecimento',
        //   description: 'O agente responde dúvidas com base no conteúdo da conta.'
        // }
      ],
      profileFieldOptions: [
        {value: 'resume', name: this.$t('Resume (file)')},
        {value: 'phone', name: this.$t('Phone')},
        {value: 'email', name: this.$t('Email')},
        {value: 'headline', name: this.$t('Intended position')},
        {value: 'address', name: this.$t('Address')},
        {value: 'birth_date', name: this.$t('Birth date')},
        {value: 'summary', name: this.$t('Qualifications summary')},
        {value: 'gender', name: this.$t('Gender')},
        {value: 'disability', name: this.$t('Disability')},
        {value: 'salary', name: this.$t('Salary')},
        {value: 'position_category', name: this.$t('Professional category')},
        {value: 'experience_level', name: this.$t('Experience level')},
        {value: 'educations', name: this.$t('Educations')},
        {value: 'experiences', name: this.$t('Experiences')},
        {value: 'pages', name: this.$t('Pages (Social Networks)')},
        {value: 'languages', name: this.$t('Languages')},
        {value: 'photo', name: this.$t('Photo')}
      ]
    }
  },
  methods: {
    /*
    error
    resolve a mensagem de validacao da api pelo caminho do campo
    */
    error (path) {
      return this.validateErrors[`${this.errorPrefix}.${path}`]
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Account/AccountConversationalAgentCandidate.vue